// reportsContract — what the Office Reports screen is allowed to say, declared
// once and checked against the route files by OfficeReportsDetail.test.js.
//
// CLAUDE.md, "Declare a contract once, and check it against the server": a
// table read off the route file beats the same rules hardcoded in a screen,
// because a screen hardcoding them ends up offering a control the server
// answers 400 to — or, worse here, a control the server silently ignores.
//
// ── THE CONTRACT, read off the route files (2026-09-04) ─────────────────────
// Every name below is in those files. Nothing here is inferred from a name.
//
//   GET /api/time-entries/reports/summary      server/routes/timeEntries.js:561
//     GATE  authMiddleware only. The handler narrows to the caller's own rows
//           for `role === 'worker'` AND FOR NO OTHER ROLE (:583). So admin,
//           manager AND accounting all get the whole company here, and the
//           `user_id` parameter is honoured for all three.
//     ?user_id ?job_id ?start_date ?end_date   — all optional; the dates are
//           YYYY-MM-DD in the COMPANY timezone, converted by
//           buildLocalDateRangeFilter.
//     ->  [ { job_id, job_title, user_id, user_name, entry_count,
//             total_duration } ]   grouped by job x user, desc by duration.
//         `total_duration` is SUM(te.duration) in SECONDS. A running entry has
//         a NULL duration, which SUM skips while COUNT(te.id) still counts it,
//         so entry_count can exceed the number of entries the seconds cover.
//
//   GET /api/time-entries                      server/routes/timeEntries.js:26
//     GATE  authMiddleware only — BUT the handler narrows to the caller's own
//           rows for every role that is not admin or manager (:41), and in that
//           branch `user_id` is not read at all (it is the `else if`).
//
//           THAT ASYMMETRY IS THE WHOLE REASON THIS FILE EXISTS. The two
//           endpoints this screen reads gate DIFFERENTLY: `accounting` gets the
//           whole company's summary and only their OWN detailed entries. A
//           bookkeeper — the person Office was built for — therefore sees a
//           company-wide summary above and, below it, an empty detail list that
//           reads as "the crew logged nothing", plus a Worker filter that
//           appears to work and does nothing. Neither is an error; both are
//           false statements rendered as data.
//     ?job_id ?user_id ?start_date ?end_date
//     ->  [ te.*, job_title, user_name, user_email ]  ordered start_time DESC.
//         te.* is every column of time_entries: id, user_id, job_id,
//         start_time, end_time, duration, latitude, longitude, start_note,
//         pause_note, … `end_time` NULL means the entry is still running and
//         `duration` is then NULL — never 0.
//
//   GET /api/dashboard/activity-feed           server/routes/dashboard.js:353
//     GATE  authMiddleware only. Rows are everyone's for admin/manager and the
//           caller's own for every other role (:361) — the same narrowing, and
//           the same need to say so on screen.
//     ?limit   defaults to 15, parseInt'd.
//     ->  a BARE ARRAY of global_activity_log rows plus `user_name` and
//         `profile_picture`. `created_at` is a bare SQLite CURRENT_TIMESTAMP
//         ('YYYY-MM-DD HH:MM:SS', UTC, no marker) — see timeAgo below.
//
//     This is the endpoint the classic Dashboard's Recent Activity card calls
//     (client/src/components/Dashboard/Dashboard.js:348), and it is used here
//     in preference to GET /api/activity/recent, which the rebuild screen used
//     to call. `/activity/recent` is behind an inline
//     `role !== 'admin' && role !== 'manager'` 403 (server/routes/activity.js:196),
//     so on Office — a screen the navigation offers to accounting — it was a
//     guaranteed refusal on every single load for that role. The full audit log
//     with its filters stays on insight/Activity.js, which is gated to the
//     roles that endpoint admits.

import { parseUTCDate } from '../../../utils/timezone';
import { gpsText } from '../../shared/geoFix';

// ── paths and parameters ────────────────────────────────────────────────────

export const SUMMARY_PATH = '/time-entries/reports/summary';
export const ENTRIES_PATH = '/time-entries';
export const FEED_PATH = '/dashboard/activity-feed';

/** Every query parameter each handler destructures out of req.query. */
export const SUMMARY_PARAMS = ['user_id', 'job_id', 'start_date', 'end_date'];
export const ENTRIES_PARAMS = ['job_id', 'user_id', 'start_date', 'end_date'];
export const FEED_PARAMS = ['limit'];

/**
 * The CAPABILITIES each handler reads, since 2026-09-09.
 *
 * These were role lists — ENTRIES_ALL_ROLES = ['admin','manager'] and
 * SUMMARY_SELF_ROLES = ['worker'] — mirroring what the routes asked. The
 * permissions audit changed what the routes ask, and it fixed a real bug in
 * doing so: `role === 'worker' ? own : everything` handed the FULL company
 * summary to accounting (who hold `reports: ['read','export']` and no clock
 * oversight at all) and to anybody in a custom role, who is not called
 * 'worker' either.
 *
 * So the scope notes below take the ANSWER rather than the role. A note
 * derived from a role would tell somebody holding a grant that their list is
 * scoped when it is not — a lie in the one place the screen exists to be
 * honest about.
 */
export const ENTRIES_ALL_CAPS = [['time_entries', 'manage_all'], ['time_entries', 'manage_team']];
export const SUMMARY_ALL_CAPS = [...ENTRIES_ALL_CAPS, ['reports', 'read']];

/** Kept as documentation of who holds those capabilities in the seeded matrix. */
export const ENTRIES_ALL_ROLES = ['admin', 'manager'];
export const SUMMARY_SELF_ROLES = ['worker'];

/** Roles GET /api/dashboard/activity-feed answers for everyone. */
export const FEED_ALL_ROLES = ['admin', 'manager'];

/** Rows asked of the feed. The handler's own default is 15. */
export const FEED_LIMIT = 20;

/** The two shapes this screen can report the same range in. */
export const VIEWS = [
  { key: 'summary', label: 'Summary by job' },
  { key: 'detailed', label: 'Detailed entries' },
];

const DASH = '—';

function qs(params) {
  const out = new URLSearchParams();
  Object.keys(params).forEach((k) => {
    const v = params[k];
    if (v !== null && v !== undefined && v !== '') out.set(k, String(v));
  });
  return out.toString();
}

/** GET /api/time-entries/reports/summary for the current filters. Pure. */
export function summaryPath({ start, end, userId, jobId } = {}) {
  return `${SUMMARY_PATH}?${qs({ start_date: start, end_date: end, user_id: userId, job_id: jobId })}`;
}

/** GET /api/time-entries for the current filters. Pure. */
export function entriesPath({ start, end, userId, jobId } = {}) {
  return `${ENTRIES_PATH}?${qs({ start_date: start, end_date: end, user_id: userId, job_id: jobId })}`;
}

/** GET /api/dashboard/activity-feed. Pure. */
export function feedPath(limit = FEED_LIMIT) {
  const n = Math.max(1, Math.trunc(Number(limit) || FEED_LIMIT));
  return `${FEED_PATH}?${qs({ limit: n })}`;
}

// ── reading an answer ───────────────────────────────────────────────────────

/**
 * The rows of a bare-array endpoint, or NULL when the body is not one.
 *
 * NOT `data || []`. A 200 whose body is not an array is a failure wearing a
 * success, and `|| []` hands it to a table that renders "No time was tracked
 * in this range" — a measurement, made from no measurement. `undefined` (the
 * request has not answered yet) is null here too; the caller separates
 * "pending" from "unreadable" with the query's own isPending flag.
 */
export function rowsOf(data) {
  return Array.isArray(data) ? data : null;
}

// ── what the server did NOT do, said out loud ───────────────────────────────

/**
 * Why the detailed list may not be the whole company, for this role — or null
 * when it is.
 *
 * Returning null for admin and manager is the load-bearing half. Warning
 * unconditionally would pass every "must not lie" test while telling a manager
 * their complete list is incomplete, which is the same fault pointed the other
 * way.
 */
export function entriesScopeNote(seesEveryone) {
  if (seesEveryone) return null;
  return 'The server returns only your own time entries to this role, so this list '
    + 'is not the whole company and the Worker filter above does not apply to it. '
    + 'The summary view is company-wide; this one is not.';
}

/** The same, for the summary view — which is narrowed to fewer people. */
export function summaryScopeNote(seesEveryone) {
  if (seesEveryone) return null;
  return 'The server returns only your own hours to this role, so these totals are '
    + 'yours and not the company\'s.';
}

/** 'everyone' or 'self' — whose actions the activity feed will contain. */
export function feedScope(role) {
  return FEED_ALL_ROLES.includes(role) ? 'everyone' : 'self';
}

/** The sentence under the feed's title. Never null: the scope is always worth stating. */
export function feedScopeNote(role) {
  return feedScope(role) === 'everyone'
    ? 'Everything the office and the crews have done, newest first.'
    : 'Only your own actions — the server scopes this feed to you, so an empty '
      + 'list here does not mean nothing happened.';
}

/** What an empty feed means, which depends on whose feed it is. */
export function feedEmptyText(role) {
  return feedScope(role) === 'everyone'
    ? 'Nothing has been logged yet.'
    : 'You have not done anything that gets logged yet. Other people\'s actions '
      + 'are not in this list.';
}

// ── labels ──────────────────────────────────────────────────────────────────

/**
 * action_type → a word. Copied from the classic Dashboard
 * (client/src/components/Dashboard/Dashboard.js:420) so the two apps read the
 * same while both are in production.
 *
 * There is NO enum on the server — every caller of logGlobalActivity passes its
 * own string and server/routes/jobs.js passes a computed one — so an unknown
 * token falls through to a de-underscored version of itself rather than being
 * dropped. A dropped label would silently hide a whole class of event.
 */
export const ACTION_LABELS = {
  started: 'started',
  stopped: 'stopped',
  created: 'created',
  updated: 'updated',
  deleted: 'deleted',
  completed: 'completed',
  submitted: 'submitted',
  approved: 'approved',
  rejected: 'rejected',
  paused: 'paused',
  comment: 'commented',
  media_upload: 'uploaded media',
};

export function actionLabel(token) {
  const raw = token === null || token === undefined ? '' : String(token);
  if (!raw) return DASH;
  if (ACTION_LABELS[raw]) return ACTION_LABELS[raw];
  return raw.replace(/[_-]+/g, ' ').trim() || DASH;
}

/**
 * "just now" / "5m ago" / "3h ago" / "2d ago", or a null when the stamp cannot
 * be read.
 *
 * Through parseUTCDate, NOT `new Date(str)`. `global_activity_log.created_at`
 * is a bare SQLite CURRENT_TIMESTAMP — "2026-08-14 23:30:00", space separator,
 * no zone marker — and this endpoint returns `gal.*` untouched. `new Date()`
 * reads that as LOCAL, which in Eastern puts every row four hours in the
 * FUTURE, so the difference goes negative and the whole first four hours of the
 * feed collapse into "just now". Measured and written down on the classic
 * screen (Dashboard.js:426); the rebuild must not reintroduce it.
 *
 * Returns null rather than a guess for an unreadable stamp, so the caller can
 * fall back to the absolute datetime instead of printing a confident "just now"
 * over a date nobody could parse.
 */
export function timeAgo(stamp, now = Date.now()) {
  const parsed = parseUTCDate(stamp);
  if (!parsed || Number.isNaN(parsed.getTime())) return null;
  const seconds = (now - parsed.getTime()) / 1000;
  if (!Number.isFinite(seconds)) return null;
  if (seconds < 0) return 'just now';       // a clock ahead of ours, not the future
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 86400 * 30) return `${Math.floor(seconds / 86400)}d ago`;
  return null;                              // older than a month: show the date
}

// ── one time entry, as text ─────────────────────────────────────────────────

/** Is this entry still running? end_time NULL is the server's marker for it. */
export function isRunning(entry) {
  return !!entry && (entry.end_time === null || entry.end_time === undefined || entry.end_time === '');
}

/**
 * How a SUMMARY row should read its hours, given that some of the shifts in it
 * may still be running.
 *
 * The detail view has always got this right — `isRunning(e) ? DASH : hoursOf(e.duration)`
 * — with a comment saying "0.00 in an hours column is a claim that somebody
 * worked none, the exact reading a crew member disputes on payday". The
 * SUMMARY had no way to know: it receives an aggregate, `duration` DEFAULTs to
 * 0 and is only written when a shift is stopped, so a job somebody is working
 * right now reported `1 entry, 0.00 hours`.
 *
 * `running_count` (added to GET /time-entries/reports/summary alongside the
 * existing aggregates, changing none of them) is what makes the three cases
 * distinguishable:
 *
 *   nothing running          the completed total, exactly as before
 *   some running             the completed total, marked `+` — those hours ARE
 *                            real and banked; there is simply more coming
 *   everything running       a dash. Nothing has been completed, and a number
 *                            here would be that same false claim
 *
 * The `+` rather than a word because this is a right-aligned tabular numeric
 * cell in a column of them; a sentence would break the column and be truncated
 * on a phone anyway. `summaryHoursTitle` supplies the words for a tooltip and
 * for a screen reader.
 *
 * Pure, and exported separately from the render so it can be tested without a
 * DOM — the arithmetic it does NOT do is the point.
 */
export function summaryHours(row, hoursOf) {
  const running = Number(row && row.running_count) || 0;
  const entries = Number(row && row.entry_count) || 0;
  const completed = hoursOf(row ? row.total_duration : 0);
  if (running <= 0) return completed;
  if (entries > 0 && running >= entries) return DASH;
  return `${completed}+`;
}

/** The sentence behind `summaryHours`, for a title attribute and a label. */
export function summaryHoursTitle(row) {
  const running = Number(row && row.running_count) || 0;
  if (running <= 0) return '';
  const entries = Number(row && row.entry_count) || 0;
  const plural = running === 1 ? 'shift is' : 'shifts are';
  if (entries > 0 && running >= entries) {
    return `No completed hours yet — ${running} ${plural} still running.`;
  }
  return `Completed hours so far. ${running} ${plural} still running.`;
}

/** How many shifts in a row the `total_duration` seconds actually cover. */
function completedEntries(row) {
  const entries = Number(row && row.entry_count) || 0;
  const running = Number(row && row.running_count) || 0;
  return Math.max(0, entries - running);
}

/**
 * The mean shift length in a summary row — the classic screen's "Avg per
 * Entry" column (client/src/components/Reports/Reports.js:556) and its
 * `Average per Entry (Hours)` CSV column (:272), both of which the rebuild
 * dropped. docs/PARITY_2026-09-08.md §10.
 *
 * ── It divides by the COMPLETED shifts, not by entry_count ──────────────────
 *
 * The classic screen wrote `total_duration / entry_count`. That is wrong
 * whenever anybody is on the clock, and wrong in the direction that matters:
 * SUM(te.duration) SKIPS a running entry (duration is NULL until clock-out)
 * while COUNT(te.id) counts it, so the numerator covers fewer shifts than the
 * denominator names. A row with two finished two-hour shifts and one running
 * reports 1.33 rather than 2.00 — a number that is not true of any of the
 * three shifts. This is the same asymmetry `summaryHours` above exists for,
 * and it is documented on the endpoint in this file's header.
 *
 * So: seconds over completed shifts, and a DASH when there are no completed
 * shifts to average. `0.00` there would be a claim that a shift averaged no
 * time, about somebody who is out there working — the exact reading this repo
 * keeps recording as worse than an error.
 *
 * ── What is NOT here, and why: Estimated and Variance ───────────────────────
 *
 * §10 also asks for the classic screen's `Estimated` and `Variance` columns,
 * which read `jobs.estimated_time` off GET /api/jobs/all. They are still not
 * restored, but the reason has changed and this note is corrected rather than
 * left to be followed (CLAUDE.md: a note that has quietly become false is
 * worse than no note).
 *
 * WHAT IT SAID: the column held two incompatible units — the rebuilt form
 * wrote the person's MINUTES verbatim while four server routes divided the
 * same column by 3600 — so no client-side reading of it could be right for
 * every row, and the decision needed a human.
 *
 * WHAT IS TRUE NOW (#620, migration 0006_estimated_time_seconds):
 *
 *   · The column means SECONDS. That is what dispatch.js:70, field.js:165 and
 *     timesheets.js:445/:676 always assumed, what every pre-rebuild row holds,
 *     and what utils/agenda.js `jobDuration` already compared against
 *     total_time.
 *   · The form still asks a person for minutes and converts: jobFields.js
 *     `estimatedSeconds` on the way out, `estimatedMinutes` on the way in.
 *   · 0006 converted the rows the rebuilt form had written in minutes.
 *
 * SO THE REMAINING BLOCKER IS NARROWER, AND IT IS NOT A UNIT QUESTION.
 * 0006 deliberately did NOT convert the rows whose unit nothing in the data
 * could settle — jobs created while both apps were live behind /classic, jobs
 * backdated by an admin, and recurrence copies of pre-cutover series. Those
 * rows are recorded in `job_estimate_unit_audit` and listed by
 * `node scripts/estimate-units.js <db>`, and each needs a person to look at it.
 * Until that list is worked through, an Estimated/Variance column would put a
 * possibly-60x-wrong figure on the report the office uses to ask whether a job
 * is losing money — for a KNOWN and ENUMERABLE set of jobs rather than for all
 * of them. Restoring the columns is one afternoon after that list is empty;
 * the average below needs none of it.
 *
 * @param row      one summary row
 * @param hoursOf  the app's own seconds -> '0.00' formatter, passed in so this
 *                 stays pure and does no unit arithmetic of its own
 */
export function avgPerEntry(row, hoursOf) {
  const n = completedEntries(row);
  if (n <= 0) return DASH;
  return hoursOf((Number(row && row.total_duration) || 0) / n);
}

/** The sentence behind `avgPerEntry`, for a title attribute and a label. */
export function avgPerEntryTitle(row) {
  const running = Number(row && row.running_count) || 0;
  if (running <= 0) return '';
  const n = completedEntries(row);
  const plural = running === 1 ? 'shift is' : 'shifts are';
  if (n <= 0) return `No completed shifts yet — ${running} ${plural} still running.`;
  return `The average over ${n} completed shift${n === 1 ? '' : 's'}. ${running} ${plural} still running.`;
}

/**
 * "41.7600, -72.6800" for an entry that carries a fix, '' for one that does
 * not.
 *
 * MOVED to apps/shared/geoFix.js on 2026-09-09 and re-exported here, so every
 * caller and OfficeReportsDetail.test.js are untouched. Same move, and the
 * same reason, as `serverSentence` → apps/shared/http.js: the live board is a
 * DIFFERENT section and needs the same reading, and
 * sectionsDoNotCrossImport.test.js refuses `dispatch` importing from `office`
 * — correctly. A thing two sections need is an app thing.
 *
 * The Number.isFinite guard, and why truthiness is wrong for a coordinate
 * (a 0 latitude is a real place), are written up there. One behaviour is
 * added: geoFix also reads the `lat`/`lng` spelling routes/dispatch.js aliases
 * the same two columns to. A Reports row carries `latitude`/`longitude` and is
 * unaffected.
 *
 * Imported AND re-exported, not `export … from`: the CSV builder below calls
 * gpsText, and a bare re-export creates no local binding for it to call.
 */
export { gpsText };

/** Both notes on one entry, in the classic screen's order. */
export function noteBits(entry) {
  const bits = [];
  if (!entry) return bits;
  if (entry.start_note) bits.push(String(entry.start_note));
  if (entry.pause_note) bits.push(String(entry.pause_note));
  return bits;
}

// ── CSV ─────────────────────────────────────────────────────────────────────

/**
 * The detailed-entries CSV.
 *
 * The header row is the classic screen's, character for character
 * (client/src/components/Reports/Reports.js:293), because both apps are in
 * production against the same database and a spreadsheet someone already built
 * against `time-entries.csv` must keep working. This invents no second format.
 *
 * The one deliberate difference is the Duration cell for a RUNNING entry.
 * `duration` is NULL until the entry is stopped; the classic export writes
 * `(null / 3600).toFixed(2)` and so puts the literal text `NaN` in the cell.
 * Blank is the truth — the shift is not over — and it is the same choice the
 * classic screen already made for its Estimated column when the job list
 * failed: a spreadsheet cell reading 0.00 is a claim that no time was worked.
 *
 * @param {Array} rows      what GET /api/time-entries answered
 * @param {object} fmt      { date, time, hours } — the app's own formatters,
 *                          passed in so this stays pure and testable.
 * @param {function} line   the CSV line writer (OfficePage's csvLine)
 */
export function entriesCsv(rows, fmt, line) {
  const headers = ['Date', 'Job', 'User', 'Start Time', 'End Time', 'Duration (Hours)', 'Location', 'Start Note', 'Pause Note'];
  const out = [line(headers)];
  (rows || []).forEach((e) => {
    const running = isRunning(e);
    out.push(line([
      fmt.date(e.start_time),
      e.job_title || 'Unknown',
      e.user_name || 'Unknown',
      fmt.time(e.start_time),
      running ? 'Active' : fmt.time(e.end_time),
      running ? '' : fmt.hours(e.duration),
      gpsText(e),
      e.start_note || '',
      e.pause_note || '',
    ]));
  });
  return `${out.join('\n')}\n`;
}

/** The filename both exports use, so the range is on the file and not only on the screen. */
export function csvName(view, range) {
  const stem = view === 'detailed' ? 'time-entries' : 'hours';
  return `${stem}-${range.start}-to-${range.end}.csv`;
}
